import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { CodeSentryFetcherName, fetcherToPlainText, fossaFetchersMap } from '../../../../../shared/fetchers';
import { FOSSABOT_SUPPORTED_FETCHERS } from '../../../../../shared/fossabot/common';
import { DependencyUsage, dependencyUsageFromDepth } from '../../../../../shared/issuesV2/common';
import { isProjectScope } from '../../../../../shared/issuesV2/helpers';
import { useStartGitHubAppInstall } from '../../../../hooks/api/vcs/githubApp/useStartGitHubAppInstall';
import { getErrorMessage } from '../../../../ReactQuery/errors';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import DetailsPanel from '../../../generic/DetailsPanel';
import VulnerabilityDrawerSection from '../../../shared/vulns/Drawer/Section';
import { getRemediationCardFlags } from '../../../shared/vulns/RemediationPanels/getRemediationCardFlags';
import RemediationSuggestion from '../../../shared/vulns/RemediationPanels/RemediationSuggestion';
import type { Fetcher } from '../../../../../shared/fetchers';

import { FossabotBlockedCaption } from './FossabotBlockedCaption';
import { FossabotConnectionBanner } from './FossabotConnectionBanner';
import { FossabotFixBox } from './FossabotFixBox';
import { FossabotNoFixBox } from './FossabotNoFixBox';
import { FossabotOtherFixesBox } from './FossabotOtherFixesBox';
import { FossabotProjectScopePrompt } from './FossabotProjectScopePrompt';
import { isIssueDetectedViaCpe } from './isIssueDetectedViaCpe';
import { useFossabotConnection } from './useFossabotConnection';
import { useFossabotNoFix } from './useFossabotNoFix';
import { useFossabotPrFlow } from './useFossabotPrFlow';
import type { FossabotBlockedCaptionProps } from './FossabotBlockedCaption';
import type { FossabotPrFlow } from './useFossabotPrFlow';

import styles from './FossabotRemediationPanel.module.less';

/** Why Create PR is disabled when the repository is not connected; the banner's Connect button is the CTA. */
const NOT_CONNECTED_REASON = 'Connect fossabot to create PRs';

const SUPPORTED_ECOSYSTEMS_URL = 'https://docs.fossa.com/docs/fossabot/supported-ecosystems';

/*
  TODO(CORE-7942): the final "Learn more." target and the copy on the manual,
  transitive, snippet and no-repository project captions are interim, pending
  design sign-off.
*/
const LEARN_MORE_LINK = {
  linkText: 'Learn more.',
  href: 'https://docs.fossa.com/docs/fossabot',
};

/** Why Create PR is not offered on a CLI-uploaded project: there is no connected repository to open a PR on. */
const CLI_PROJECT_CAPTION: FossabotBlockedCaptionProps = {
  text: 'Auto PRs aren’t available for CLI projects.',
  ...LEARN_MORE_LINK,
};

/** Why Create PR is not offered on an SBOM project: same as a CLI project, there is no connected repository. */
const SBOM_PROJECT_CAPTION: FossabotBlockedCaptionProps = {
  text: 'Auto PRs aren’t available for SBOM projects.',
  ...LEARN_MORE_LINK,
};

/** Why Create PR is not offered on any other project that did not come from a repository, such as an uploaded archive. */
const NO_REPOSITORY_PROJECT_CAPTION: FossabotBlockedCaptionProps = {
  text: 'Auto PRs aren’t available for this project.',
  ...LEARN_MORE_LINK,
};

/** Why Create PR is not offered on a vendored dependency: same as a snippet, there is no manifest entry to bump. */
const VENDORED_CAPTION: FossabotBlockedCaptionProps = {
  text: 'Auto PRs aren’t available for Vendored Dependencies.',
  ...LEARN_MORE_LINK,
};

/** Why Create PR is not offered on a manually added dependency: it was added in FOSSA, so no manifest declares it. */
const MANUAL_CAPTION: FossabotBlockedCaptionProps = {
  text: 'Auto PRs aren’t available for manually added dependencies.',
  ...LEARN_MORE_LINK,
};

/** Why Create PR is not offered on a transitive dependency: fossabot only edits the manifest. */
const TRANSITIVE_CAPTION: FossabotBlockedCaptionProps = {
  text: 'Auto PRs aren’t available for transitive dependencies.',
  ...LEARN_MORE_LINK,
};

/** Why Create PR is not offered on a snippet match: the code was copied in, not declared in a manifest. */
const SNIPPET_CAPTION: FossabotBlockedCaptionProps = {
  text: 'Auto PRs aren’t available for snippets.',
  ...LEARN_MORE_LINK,
};

/** Vendored dependencies are uploaded by the CLI as archives. */
const VENDORED_FETCHER = 'archive';

/** Manually added dependencies get the user fetcher. */
const MANUAL_FETCHER = 'user';

/** Why Create PR is not offered for a dependency whose ecosystem fossabot does not support. */
const getUnsupportedEcosystemCaption = (fetcher: string): FossabotBlockedCaptionProps => {
  const ecosystem = fetcherToPlainText(fetcher as Fetcher) ?? fetcher;
  return {
    text: `Auto PRs aren’t available for ${ecosystem} dependencies.`,
    linkText: 'See supported ecosystems.',
    href: SUPPORTED_ECOSYSTEMS_URL,
  };
};

/** The fix cards still render when not connected, in their idle state with Create PR disabled. */
const NOT_CONNECTED_FLOW: FossabotPrFlow = {
  status: 'none',
  vcsUrl: null,
  fossabotUrl: null,
  onCreatePR: () => undefined,
};

/**
 * A blocked PR shows the standard remediation cards with a caption saying why,
 * without mounting the connection or PR status queries.
 */
const FossabotBlockedSection: VoidFunctionComponent<{ caption: FossabotBlockedCaptionProps }> = ({ caption }) => {
  const issue = useSelector(IssuesV2Store.selectors.getIssueDetails);

  /* Only vulnerability issues carry remediation; malware issues have none. */
  if (issue?.type !== 'vulnerability') {
    return null;
  }

  const {
    completeFix,
    completeFixDistance,
    partialFix,
    partialFixDistance,
  } = issue.remediation;
  const {
    shouldShowCompleteFix,
    shouldShowPartialFix,
    shouldShowNoFix,
    shouldShowUnableToCalculate,
  } = getRemediationCardFlags({
    completeFix,
    partialFix,
    isUserDefinedDependency: issue.source?.packageManager === MANUAL_FETCHER,
  });

  return (
    <>
      <div className={styles.blockedCards}>
        {shouldShowPartialFix && (
          <RemediationSuggestion type="partial" version={partialFix} upgradeDistance={partialFixDistance} useFixBadge />
        )}
        {shouldShowCompleteFix && (
          <RemediationSuggestion type="complete" version={completeFix} upgradeDistance={completeFixDistance} useFixBadge />
        )}
        {shouldShowNoFix && !shouldShowUnableToCalculate && <RemediationSuggestion type="no-fix" />}
        {shouldShowUnableToCalculate && (
          <RemediationSuggestion type="unable-to-calculate" isDetectedViaCpe={isIssueDetectedViaCpe(issue)} />
        )}
      </div>
      <FossabotBlockedCaption {...caption} />
    </>
  );
};

/**
 * The connection banner and the fix cards. Only mounts once nothing blocks a
 * PR, so the connection and PR status reads never run for a dependency
 * fossabot cannot touch.
 *
 * While the connection status is loading, the banner shows alone. Not
 * connected, or the status check failed and we cannot tell: the same fix
 * cards, with Create PR disabled and the banner's Connect button as the CTA.
 */
const FossabotPrSection: VoidFunctionComponent<{ projectLocator: string }> = ({ projectLocator }) => {
  const { mutate: startInstall } = useStartGitHubAppInstall();
  const connection = useFossabotConnection(projectLocator);
  const isConnected = connection.state === 'connected';
  const prFlow = useFossabotPrFlow(isConnected);

  const flow = isConnected ? prFlow : NOT_CONNECTED_FLOW;
  const disabledReason = isConnected ? undefined : NOT_CONNECTED_REASON;

  return (
    <>
      <FossabotConnectionBanner
        state={connection.state}
        errorMessage={getErrorMessage(connection.error, 'Could not check fossabot connection status.')}
        onConnect={() => startInstall('fossabot')}
      />
      {connection.state !== 'checking' && flow && (
        <>
          <FossabotFixBox
            status={flow.status}
            vcsUrl={flow.vcsUrl}
            fossabotUrl={flow.fossabotUrl}
            onCreatePR={flow.onCreatePR}
            errorMessage={flow.errorMessage}
            onRetry={flow.onRetry}
            disabledReason={disabledReason}
          />
          <FossabotOtherFixesBox onCreatePR={flow.onCreatePR} disabledReason={disabledReason} />
        </>
      )}
    </>
  );
};

/**
 * The fossabot remediation panel, shown for vulnerability issues when the
 * fossabotDepUpgradePRs flag is on and the user can view fossabot upgrades.
 * Renders the collapsible Remediation section with the fossabot connection
 * banner bleeding edge to edge across the panel.
 */
type Props = {
  /** Switches the drawer to the Projects tab, where each affected project links to project scope. */
  onViewProjects: () => void;
};

/**
 * What the Remediation section shows: the standard remediation cards over the
 * first blocked caption, else the connection banner and fix cards.
 *
 * A fossabot PR targets exactly one repository, so outside project scope
 * (global / release group) the user is pointed at project scope. A project on
 * one of FOSSA's own fetchers (the list in shared/fetchers.ts) is an upload with
 * no repository, so it is blocked, and so is any fetcher added there. An issue with
 * no package manager, a project the issue does not list, or an unknown depth
 * all fail open to the fix cards.
 */
const FossabotRemediationContent: VoidFunctionComponent<Props> = ({ onViewProjects }) => {
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const issue = useSelector(IssuesV2Store.selectors.getIssueDetails);
  const noFixType = useFossabotNoFix();

  if (!isProjectScope(scope)) {
    return <FossabotProjectScopePrompt onViewProjects={onViewProjects} />;
  }

  const fetcher = issue?.source?.packageManager;
  const depth = issue?.projects.find(({ id }) => id === scope.id)?.depth ?? null;
  /* The project's own fetcher, read off its locator. `fetcher` above is the vulnerable dependency's. */
  const [projectFetcher = ''] = scope.id.split('+');

  if (projectFetcher === 'custom') {
    return <FossabotBlockedSection caption={CLI_PROJECT_CAPTION} />;
  }
  if (projectFetcher === 'sbom') {
    return <FossabotBlockedSection caption={SBOM_PROJECT_CAPTION} />;
  }
  if (projectFetcher in fossaFetchersMap) {
    return <FossabotBlockedSection caption={NO_REPOSITORY_PROJECT_CAPTION} />;
  }
  if (fetcher === VENDORED_FETCHER) {
    return <FossabotBlockedSection caption={VENDORED_CAPTION} />;
  }
  if (fetcher === MANUAL_FETCHER) {
    return <FossabotBlockedSection caption={MANUAL_CAPTION} />;
  }
  if (fetcher && !FOSSABOT_SUPPORTED_FETCHERS.has(fetcher)) {
    return <FossabotBlockedSection caption={getUnsupportedEcosystemCaption(fetcher)} />;
  }
  if (dependencyUsageFromDepth(depth) === DependencyUsage.Deep) {
    return <FossabotBlockedSection caption={TRANSITIVE_CAPTION} />;
  }
  if (issue?.analysisSnippetId) {
    return <FossabotBlockedSection caption={SNIPPET_CAPTION} />;
  }
  if (noFixType !== null) {
    return <FossabotNoFixBox type={noFixType} />;
  }
  return <FossabotPrSection projectLocator={scope.id} />;
};

export const FossabotRemediationPanel: VoidFunctionComponent<Props> = ({ onViewProjects }) => {
  const issue = useSelector(IssuesV2Store.selectors.getIssueDetails);

  /*
    Matches the sibling sections (Remediation, Header): with no issue in Redux
    there is nothing to remediate, so render nothing rather than an empty card.
    Binary decomposition (csbinary) dependencies never had a Remediation
    section, so keep hiding it rather than showing an unsupported notice.
  */
  if (!issue || issue.source?.packageManager === CodeSentryFetcherName) {
    return null;
  }

  return (
    <VulnerabilityDrawerSection>
      <DetailsPanel id="fossabot-remediation" target="Remediation" isUnpadded>
        <div data-testid="fossabot-remediation-panel">
          <FossabotRemediationContent onViewProjects={onViewProjects} />
        </div>
      </DetailsPanel>
    </VulnerabilityDrawerSection>
  );
};
